import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./context/authContext";
import axios from "axios";

// helper to produce/get a device id (same logic used in Supervisora)
const getDeviceIdLocal = () => {
  try {
    let id = localStorage.getItem('device_id');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : Math.random().toString(36).slice(2, 10);
      localStorage.setItem('device_id', id);
    }
    return id;
  } catch (e) { return 'unknown-device'; }
};

const extractUserStringFromData = (data) => {
  if (!data) return '';
  if (typeof data === 'string') return data;
  // try common fields
  return (data.user || data.id || data.email || data.usuario || data.username || data.name || '') + '';
};

function Login() {
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  // Al abrir la app con sesión guardada: mientras se comprueba, "Comprobando tu
  // sesión…"; si no hay conexión, "Sin conexión" y se reintenta solo (no se enseña
  // el formulario: parecería que la app ha cerrado la sesión).
  const haySesionGuardada = () => !!(localStorage.getItem('access_token') || localStorage.getItem('refresh_token'));
  const [estadoSesion, setEstadoSesion] = useState(() => (haySesionGuardada() ? 'comprobando' : 'formulario'));
  const [reintento, setReintento] = useState(0);

  // Auto-redirect: si existe access_token o refresh_token válido,
  // consultar /api/login/me y redirigir según el rol.
  useEffect(() => {
    if (estadoSesion === 'formulario') return undefined;
    let cancelled = false;
    let reloj = null;
    const tryAutoRedirect = async () => {
      if (!haySesionGuardada()) { setEstadoSesion('formulario'); return; }
      setEstadoSesion('comprobando');
      try {
        // _connRetried: sin el aviso global de reconexión; el "Sin conexión" va aquí
        const res = await axios.get('/api/login/me', { timeout: 12000, _connRetried: true });
        if (cancelled) return;
        const tipo = res?.data?.tipo;
        if (!tipo) { setEstadoSesion('formulario'); return; }
        // Poblar el authContext para que PrivateRoute no redirija de vuelta
        login(res.data);
        switch (tipo) {
          case 'superadmin': navigate('/superadmin'); break;
          case 'admin_compras':
          case 'admin_servicios':
          case 'admin_asistentas':
          case 'admin_atcliente': navigate('/admin'); break;
          case 'supervisora': navigate('/supervisora'); break;
          case 'asistenta': navigate('/asistenta'); break;
          case 'comprador_jefe': navigate('/comprador_jefe'); break;
          case 'comprador': navigate('/comprador'); break;
          case 'repartidor': navigate('/repartidor'); break;
          case 'contabilidad': navigate('/contabilidad'); break;
          default: setEstadoSesion('formulario'); break;
        }
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        if (!e?.response || status >= 500) {
          // sin conexión (o el servidor no responde): la sesión sigue guardada, reintentar
          setEstadoSesion('sin_conexion');
          reloj = setTimeout(() => setReintento((n) => n + 1), 5000);
        } else {
          // sesión caducada / rechazada (el interceptor ya la ha limpiado): a entrar
          setEstadoSesion('formulario');
        }
      }
    };
    tryAutoRedirect();
    const alVolverLaRed = () => setReintento((n) => n + 1);
    window.addEventListener('online', alVolverLaRed);
    return () => { cancelled = true; clearTimeout(reloj); window.removeEventListener('online', alVolverLaRed); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reintento]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      
      // Use same-origin relative URL so the browser will talk to nginx (TLS terminates there)
      // This avoids mixed-content errors when the site is served over HTTPS.
      const { data } = await axios.post(`/api/login/`, {
        user,
        password,
      }, { withCredentials: true });

      // Guardar tokens + datos de usuario vía authContext
      login(data);

      // Garantizar que device_id está guardado para notificaciones push
      try { getDeviceIdLocal(); } catch (_) {}

      // Redirección según rol
      switch (data.tipo) {
        case "superadmin":
          navigate("/superadmin");
          break;
        case "admin_compras":
        case "admin_servicios":
        case "admin_asistentas":
        case "admin_atcliente":
          navigate("/admin");
          break;
        case "supervisora":
          navigate("/supervisora");
          break;
        case "comprador_jefe":
          navigate("/comprador_jefe");
          break;
        case "comprador":
          navigate("/comprador");
          break;
        case "repartidor":
          navigate("/repartidor");
          break;
        case "asistenta":
          navigate("/asistenta");
          break;
        case "contabilidad":
          navigate("/contabilidad");
          break;
        default:
          setMessage("Tipo de usuario desconocido");
      }
    } catch (error) {
      if (error.response) {
        setMessage(`❌ ${error.response.data.message}`);
      } else {
        setMessage("❌ Error de conexión. Intenta más tarde.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (estadoSesion !== 'formulario') {
    const sinRed = estadoSesion === 'sin_conexion';
    return (
      <div style={styles.container}>
        <img src="/logo_ch_bien-512.png" alt="Campus Home" style={styles.logo} />
        <div style={{ textAlign: 'center', maxWidth: 340, margin: '0 auto' }}>
          <div style={{ fontSize: 44, marginBottom: 6 }}>{sinRed ? '📶' : '⏳'}</div>
          <h2 style={{ ...styles.title, marginBottom: 8 }}>{sinRed ? 'Sin conexión' : 'Comprobando tu sesión…'}</h2>
          <p style={{ color: '#475569', fontSize: 16, lineHeight: 1.5, margin: '0 0 18px' }}>
            {sinRed
              ? 'No se ha podido conectar. Tu sesión sigue guardada: en cuanto vuelva internet entrarás solo.'
              : 'Un momento, enseguida entras.'}
          </p>
          {sinRed && (
            <button onClick={() => setReintento((n) => n + 1)} style={{ ...styles.button, width: '100%' }}>
              Reintentar
            </button>
          )}
          <button
            onClick={() => setEstadoSesion('formulario')}
            style={{ marginTop: 14, background: 'none', border: 'none', color: '#64748b', textDecoration: 'underline', fontSize: 14, cursor: 'pointer' }}
          >
            Entrar con otro usuario
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <img src="/logo_ch_bien-512.png" alt="Campus Home" style={styles.logo} />
      <h2 style={styles.title}>Iniciar sesión</h2>
      <form onSubmit={handleLogin} style={styles.form}>
        <input
          type="text"
          placeholder="Usuario"
          value={user}
          onChange={(e) => setUser(e.target.value)}
          required
          style={styles.input}
          disabled={loading}
        />
        <input
          type="password"
          placeholder="Contraseña"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={styles.input}
          disabled={loading}
        />
        <button type="submit" style={styles.button} disabled={loading}>
          {loading ? "Cargando..." : "Entrar"}
        </button>
      </form>
      {message && <p style={styles.message}>{message}</p>}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 400,
    margin: "50px auto",
    padding: 30,
    borderRadius: 8,
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: "#fff",
  },
  logo: {
    display: "block",
    width: 160,
    height: 160,
    borderRadius: 28,
    margin: "0 auto 20px",
  },
  title: {
    textAlign: "center",
    marginBottom: 20,
    color: "#333",
  },
  form: {
    display: "flex",
    flexDirection: "column",
  },
  input: {
    padding: 12,
    marginBottom: 15,
    borderRadius: 4,
    border: "1px solid #ccc",
    fontSize: 16,
  },
  button: {
    padding: 12,
    borderRadius: 4,
    border: "none",
    backgroundColor: "#007bff",
    color: "#fff",
    fontWeight: "bold",
    fontSize: 16,
    cursor: "pointer",
    transition: "background-color 0.3s ease",
  },
  message: {
    marginTop: 20,
    textAlign: "center",
    fontWeight: "bold",
  },
};

export default Login;
